import React from 'react'
import type { CsPersonSearchResultDto } from '@/api/person/models/searchCs/SearchPersonsCsDtos'
import type { PersonNetworkImportStatusSummaryDto } from '@/api/person/PersonApi'
import ProfilePic from '../../common/person/ProfilePic'
import LinkedInLink from '../../../components/LinkedInLink'
import { formatPersonName } from '@/utils/personUtils'
import {
    PersonNetworkImportLogStatusBadge,
    PersonNetworkImportStatusBadge,
} from '../../../components/dataManagement/PersonNetworkImportBadges'

type Props = {
    csMatches: CsPersonSearchResultDto[]
    selectedCsPersonId: string | null
    onSelectMatch: (person: CsPersonSearchResultDto) => void
    isActiveCall: boolean
    csNetworkImportStatuses: Record<string, PersonNetworkImportStatusSummaryDto>
    isLoadingNetworkStatuses: boolean
    csNetworkConnectionCounts: Record<string, number>
    isLoadingNetworkConnectionCounts: boolean
}

export default function LinkedInMatchesList({
    csMatches,
    selectedCsPersonId,
    onSelectMatch,
    isActiveCall,
    csNetworkImportStatuses,
    isLoadingNetworkStatuses,
    csNetworkConnectionCounts,
    isLoadingNetworkConnectionCounts,
}: Props): React.JSX.Element {
    return (
        <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
                Select the profile to pass into Kit.
            </p>
            <div className="space-y-2">
                {csMatches.map((result) => {
                    const isSelected = result.id === selectedCsPersonId
                    const imported = Boolean(result.existingPersonId)
                    const resolvedPersonId =
                        (result.existingPersonId ?? result.id)?.trim() ?? null
                    const statusSummary = resolvedPersonId
                        ? csNetworkImportStatuses[resolvedPersonId]
                        : undefined
                    const showStatusSpinner = Boolean(
                        resolvedPersonId &&
                        !statusSummary &&
                        isLoadingNetworkStatuses,
                    )
                    const showNoStatusRowNote =
                        statusSummary?.hasStatusRow === false
                    const statusLabel = statusSummary?.status
                    const latestLogStatus = statusSummary?.latestLogStatus
                    const networkConnectionCount = result.id
                        ? csNetworkConnectionCounts[result.id]
                        : undefined
                    const showConnectionLoading = Boolean(
                        result.id &&
                        isLoadingNetworkConnectionCounts &&
                        typeof networkConnectionCount !== 'number',
                    )

                    return (
                        <button
                            key={result.id}
                            type="button"
                            onClick={() => onSelectMatch(result)}
                            disabled={isActiveCall}
                            className={`flex w-full items-start gap-3 rounded-2xl border px-4 py-3 text-left transition ${
                                isSelected
                                    ? 'border-primary bg-secondary/40'
                                    : 'border-border bg-white hover:border-primary/50'
                            }`}
                        >
                            <ProfilePic
                                src={result.linkedInProfilePicUrl}
                                size={44}
                                alt={formatPersonName(result)}
                            />
                            <div className="flex-1">
                                <p className="text-sm font-semibold">
                                    {formatPersonName(result) || 'Unknown name'}
                                </p>
                                {result.headline ? (
                                    <p className="text-xs text-muted-foreground">
                                        {result.headline}
                                    </p>
                                ) : null}
                                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                    <span>
                                        {imported
                                            ? 'Already imported'
                                            : 'Needs graph import'}
                                    </span>
                                    {result.linkedInUrl ? (
                                        <LinkedInLink
                                            url={result.linkedInUrl}
                                            ariaLabel="Open LinkedIn profile"
                                        />
                                    ) : null}
                                </div>
                                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                    <span>
                                        Work history:{' '}
                                        {result.workHistoryCount.toLocaleString()}
                                    </span>
                                    <span>
                                        Network connections:
                                        {showConnectionLoading
                                            ? ' Checking...'
                                            : typeof networkConnectionCount ===
                                                'number'
                                              ? ` ${networkConnectionCount.toLocaleString()}`
                                              : ' Unknown'}
                                    </span>
                                </div>
                                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                    <span>Network import:</span>
                                    {showStatusSpinner ? (
                                        <span>Checking...</span>
                                    ) : statusLabel ? (
                                        <PersonNetworkImportStatusBadge
                                            status={statusLabel}
                                        />
                                    ) : (
                                        <span>Unknown</span>
                                    )}
                                    {showNoStatusRowNote ? (
                                        <span>(no status row)</span>
                                    ) : null}
                                    {latestLogStatus ? (
                                        <>
                                            <span>Latest log:</span>
                                            <PersonNetworkImportLogStatusBadge
                                                status={latestLogStatus}
                                            />
                                        </>
                                    ) : (
                                        <span>Latest log: -</span>
                                    )}
                                </div>
                            </div>
                        </button>
                    )
                })}
            </div>
        </div>
    )
}
